import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useForm } from "react-hook-form";
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from "react-router-dom";
import useAlert from "../../components/useAlert";
import { useUpdateUserMutation, useUserQuery } from "../../generated/graphql";

const UserEdit: React.FC = (): JSX.Element => {
  const { id } = useParams();

  const alert = useAlert();

  const { loading, error, data } = useUserQuery({
    variables: {
      id: id!
    }
  });

  const navigate = useNavigate();

  const [updateUserMutation] = useUpdateUserMutation();
  const { register, handleSubmit, setError, formState: { errors } } = useForm();
  const onSubmit = (formData: any) => {
    updateUserMutation({
      variables: {
        ...formData,
        clientMutationId: "1",
        id: id!
      },
      onError: (e) => {
        alert.failure(e.message);
        console.error(e);
      },
      onCompleted: (response) => {
        if (response.updateUser?.user?.successful) {
          alert.success(`User ${response.updateUser.user.result?.email} has been updated successfully.`);
          navigate("/users");
        } else {
          let errors = response.updateUser?.user?.messages;
          alert.failure(`User ${response.updateUser?.user?.result?.email} could not be updated.`);
          errors?.forEach((e) => setError(e!.field!, { type: "custom", message: e!.message! }));
        }
      }
    })
  };

  if (loading || error || !data) {
    return <div>Loading...</div>;
  }

  return <Transition.Root show={true} as={Fragment}>
    <Dialog as="div" className="relative z-10" onClose={() => { navigate("/users"); return false; }}>
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                <form className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl" onSubmit={handleSubmit(onSubmit)}>
                  <div className="h-0 flex-1 overflow-y-auto">
                    <div className="bg-indigo-700 py-6 px-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-medium text-white">Edit User</Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => navigate("/users")}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-indigo-300">
                          Update the information below and click save to edit the given user.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col justify-between">
                      <div className="divide-y divide-gray-200 px-4 sm:px-6">
                        <div className="space-y-6 pt-6 pb-5">
                          <div>
                            <label htmlFor="first-name" className="block text-sm font-medium text-gray-900">
                              First name
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                id="first-name"
                                className={`${errors.firstName && "border-red-600 "}} block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                                defaultValue={data.user?.firstName}
                                {...register("firstName")}
                              />
                              {errors.firstName && <span className="sm:text-xs text-red-600">{errors.firstName.message}</span>}
                            </div>
                          </div>
                          <div>
                            <label htmlFor="last-name" className="block text-sm font-medium text-gray-900">
                              Last name
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                id="last-name"
                                className={`${errors.lastName && "border-red-600 "}} block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                                defaultValue={data.user?.lastName}
                                {...register("lastName")}
                              />
                              {errors.lastName && <span className="sm:text-xs text-red-600">{errors.lastName.message}</span>}
                            </div>
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                              Email
                            </label>
                            <div className="mt-1">
                              <input
                                type="email"
                                id="email"
                                className={`${errors.email && "border-red-600 "}} block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                                defaultValue={data.user?.email}
                                {...register("email", { required: true, minLength: 3, maxLength: 255 })}
                              />
                              {errors.email && <span className="sm:text-xs text-red-600">{errors.email.message}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 justify-end px-4 py-4">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => navigate("/users")}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="ml-4 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      Save
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Dialog>
  </Transition.Root >;
};

export default UserEdit;